import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  Zoom,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  AcUnit,       // Christmas
  Favorite,     // Valentines
  Celebration,  // New Year
  Flag,         // Independence
  Star,         // Default
  CardGiftcard,
  VolunteerActivism, // Hand holding heart
  LocalFlorist,  // Flower
  AccessTime
} from '@mui/icons-material';
import EventAnimations from './EventAnimations';

// Icon mapping
const iconMap = {
  'AcUnit': AcUnit,
  'Favorite': Favorite,
  'Celebration': Celebration,
  'Flag': Flag,
  'Star': Star,
  'VolunteerActivism': VolunteerActivism,
  'LocalFlorist': LocalFlorist
};

const EventGreetingModal = ({ open, onClose, event }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!event?.event_date || !open) return;

    const calculateTimeLeft = () => {
      const eventDate = new Date(event.event_date);
      // Set to beginning of the day (midnight)
      eventDate.setHours(0, 0, 0, 0);
      
      const now = new Date();
      const difference = eventDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft(null); // Event passed or today
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [event, open]);

  if (!event) return null;

  const handleClose = () => {
    if (dontShowAgain) {
      // Store event-specific suppression so new events will still show
      sessionStorage.setItem(`hideEvent_${event.id}`, 'true');
    }
    onClose();
  };

  // Get mapped icon or default to Star
  const MainIcon = iconMap[event.icon_name] || Star;
  
  // Dynamic Styles
  const primaryColor = event.theme_primary || '#1976d2';
  const secondaryColor = event.theme_secondary || '#dc004e';
  const textColor = event.text_color || '#ffffff';

  return (
    <>
      {open && (
        <EventAnimations 
          iconName={event.icon_name} 
          primaryColor={primaryColor} 
          secondaryColor={secondaryColor} 
        />
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          color: textColor,
          border: '2px solid rgba(255,255,255,0.2)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }
      }}
    >
      <Box sx={{ position: 'relative', p: 0 }}>
        {/* Decorative Background Icons (Subtle) */}
        <Box sx={{ position: 'absolute', top: -20, left: -20, opacity: 0.1 }}>
          <MainIcon sx={{ fontSize: 100, color: 'white' }} />
        </Box>
        <Box sx={{ position: 'absolute', bottom: -30, right: -30, opacity: 0.1 }}>
          <MainIcon sx={{ fontSize: 120, color: 'white' }} />
        </Box>

        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: textColor }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ textAlign: 'center', pt: 6, pb: 4, px: 4 }}>
            <Zoom in={open} style={{ transitionDelay: '300ms' }}>
            <Box sx={{ 
              mb: 3, 
              display: 'inline-flex', 
              p: 2, 
              bgcolor: 'rgba(255,255,255,0.2)', 
              borderRadius: '50%',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <MainIcon sx={{ fontSize: 60, color: '#fff' }} />
            </Box>
            </Zoom>

          <Typography variant="h3" gutterBottom sx={{ 
            fontFamily: 'Georgia, serif', 
            fontWeight: 'bold', 
            color: textColor, 
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)' 
          }}>
            {event.title}
          </Typography>
          
          {/* Countdown Timer */}
          {timeLeft && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Stack alignItems="center">
                <Typography variant="h4" fontWeight="bold">{timeLeft.days}</Typography>
                <Typography variant="caption">DAYS</Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold">:</Typography>
              <Stack alignItems="center">
                <Typography variant="h4" fontWeight="bold">{String(timeLeft.hours).padStart(2, '0')}</Typography>
                <Typography variant="caption">HOURS</Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold">:</Typography>
              <Stack alignItems="center">
                <Typography variant="h4" fontWeight="bold">{String(timeLeft.minutes).padStart(2, '0')}</Typography>
                <Typography variant="caption">MINS</Typography>
              </Stack>
              <Typography variant="h4" fontWeight="bold">:</Typography>
              <Stack alignItems="center">
                <Typography variant="h4" fontWeight="bold">{String(timeLeft.seconds).padStart(2, '0')}</Typography>
                <Typography variant="caption">SECS</Typography>
              </Stack>
            </Box>
          )}

          <Typography variant="h6" sx={{ 
            mb: 4, 
            color: `rgba(255,255,255,0.9)`, 
            fontStyle: 'italic',
            fontWeight: 'normal'
          }}>
            {event.message}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                bgcolor: 'white',
                color: primaryColor,
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                borderRadius: 50,
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s'
              }}
            >
              Continue to Portal
            </Button>

            <FormControlLabel
              control={
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  sx={{
                    color: `rgba(255,255,255,0.7)`,
                    '&.Mui-checked': {
                      color: 'white',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: `rgba(255,255,255,0.8)` }}>
                  Don't show this message again
                </Typography>
              }
            />
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
    </>
  );
};

export default EventGreetingModal;
